import { useState } from "react";
import { X, LifeBuoy, Clock3, Paperclip, ArrowRight } from "lucide-react";

const STEPS = [
  {
    icon: LifeBuoy,
    title: "Raise a ticket in seconds",
    body:
      "Click the “+ New ticket” button at the top of this page. Give it a short title, describe the issue in your own words, and set the priority (Low → Urgent). Our support team is notified instantly by email.",
  },
  {
    icon: Clock3,
    title: "Track SLA & status in real time",
    body:
      "Every ticket has a live SLA countdown (e.g. “12h 30m remaining”) and a coloured status badge — New → Open → In Progress → Resolved. If we’re waiting on you, the status will say “Waiting on Customer” and we’ll email you a reminder.",
  },
  {
    icon: Paperclip,
    title: "Attach files & follow the conversation",
    body:
      "Open any ticket to add comments, screenshots, or PDFs. All replies from our engineers land in the same thread and you also get an email copy — no need to remember portal URLs to keep in the loop.",
  },
];

const KEY = "jero-welcome-tour-seen";

export function shouldShowTour(user) {
  if (!user || user.role !== "customer") return false;
  if (user.must_change_password) return false; // wait until they set a real password
  try {
    return localStorage.getItem(KEY) !== "1";
  } catch {
    return false;
  }
}

export default function CustomerWelcomeTour({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  if (!open) return null;

  const finish = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    onClose();
  };

  const step = STEPS[idx];
  const Icon = step.icon;
  const isLast = idx === STEPS.length - 1;

  return (
    <div
      data-testid="welcome-tour"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(15, 42, 30, 0.55)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden"
        style={{ border: "1px solid var(--green-100)" }}
      >
        <button
          data-testid="welcome-close"
          onClick={finish}
          aria-label="Skip tour"
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100"
        >
          <X size={16} />
        </button>

        <div className="p-6 pb-4" style={{ background: "var(--green-50)" }}>
          <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--green-700)" }}>
            Welcome to Jero-One · {idx + 1} of {STEPS.length}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "var(--green-700)", color: "white" }}
            >
              <Icon size={20} />
            </div>
            <h2 className="text-lg font-bold" style={{ color: "var(--ink-900)" }}>{step.title}</h2>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm leading-relaxed" style={{ color: "var(--ink-700)" }}>
            {step.body}
          </p>
        </div>

        <div className="px-6 py-4 flex items-center justify-between border-t border-slate-100">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={`tour-dot-${i}`}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === idx ? 20 : 6,
                  background: i === idx ? "var(--green-700)" : "var(--green-100)",
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="welcome-skip"
              onClick={finish}
              className="btn btn-ghost btn-sm"
            >
              Skip
            </button>
            <button
              data-testid="welcome-next"
              onClick={() => (isLast ? finish() : setIdx(idx + 1))}
              className="btn btn-primary btn-sm"
            >
              {isLast ? "Get started" : (
                <>
                  Next <ArrowRight size={12} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
